"use client";
import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import BusinessCard from "@/components/card/businesscard";
import Pagination from "@/components/pagination/pagination";
import {
  fetchallBusinessApi,
  getBusinessesByKeyword,
  getCategoriesWithBusiness,
  getNearbyBusinesses,
} from "@/app/api/businessApi";
import { fetchAdvertisementsByStatus } from "@/app/api/advertisementApi";
import { useSearch } from "../navbar/SearchContext";
import Cookies from "js-cookie";
import ChatBotWidget from "./chatBot";
import CategoryCircle from "./categorycircle";
import { sendGAEvent } from "@next/third-parties/google";
import { getUserLocation, isNearbyKeyword } from "@/lib/locationfunction";
import { fetchBizbyCategoryIdapi } from "@/app/api/categoriesApi";
import CategoryBar from "./categorycircle";

interface Category {
  _id: string;
  name: {
    en: string;
    mr: string;
  };
  icon: string;
}
interface Business {
  categoryInfo?: any;
  _id?: string;
  slug?: string;
  logo?: { fileId?: string; url?: string };
  businessname?: string;
  categories?: (string | { en: string; mr: string })[];
  businessdescription?: string;
  websitename?: string;
  location?: { latitude: number; longitude: number };
  promoted?: boolean;
  promotedBusinessIds?: boolean;
}

interface AdImage {
  url: string;
  fileId?: string;
  _id?: string;
}

interface Ad {
  _id: string;
  images: AdImage[];
  type: string;
  startDate: string;
  endDate: string;
  businessId: string;
}

const ROTATION_INTERVAL = 5000;

export const normaliseUrl = (path: string) => {
  if (!path) return "";
  if (path.startsWith("http") || path.startsWith("/")) return path;
  return `${process.env.NEXT_PUBLIC_BACKEND_URL ?? ""}/${path.replace(
    /^\/+/,
    "",
  )}`;
};

const AdSlot: React.FC<{ images: string[] }> = ({ images }) => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    const intervalId = setInterval(
      () => setIdx((prev) => (prev + 1) % images.length),
      ROTATION_INTERVAL,
    );
    return () => clearInterval(intervalId);
  }, [images.length]);

  if (!images.length) return null;

  return (
    <Box
      w="100%"
      h={{ base: "200px", md: "220px", lg: "350px" }}
      overflow="hidden"
      borderRadius="md"
      boxShadow="md"
      flexShrink={0}
    >
      <img
        src={images[idx]}
        alt={`Ad ${idx + 1}`}
        style={{
          objectFit: "cover",
          width: "100%",
          height: "100%",
          transition: "opacity 0.5s ease-in-out",
        }}
        loading="eager"
        fetchPriority="high"
      />
    </Box>
  );
};

const BusinessList: React.FC = () => {
  const { searchName, searchCategory, searchTrigger, setSearchResults } =
    useSearch();
  const [isAnyClicked, setIsAnyClicked] = useState(false);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [adsLeft, setAdsLeft] = useState<string[][]>([[], []]);
  const [adsRight, setAdsRight] = useState<string[][]>([[], []]);
  const [adsLoading, setAdsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isAdmin, setIsAdmin] = useState(false);
  //const [logourl, setLogourl] = useState<Record<string, string | null>>({});
  const itemsPerPage = 9;
  const [promotedBusinessIds, setPromotedBusinessIds] = useState<Set<string>>(
    new Set(),
  );
  const [category, setcategory] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const [showCategories, setShowCategories] = useState(true);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const role = Cookies.get("role");
      setIsAdmin(role === "admin");
    }
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const resp = await getCategoriesWithBusiness(1, 100);
        setShowCategories(true);
        setcategory(resp.data || []);
      } catch (err) {
        console.error("Failed to fetch error", err);
      }
    };
    fetchCategories();
  }, []);
  const fetchNearbyBusinesses = async (): Promise<Business[]> => {
    return new Promise<Business[]>((resolve) => {
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try {
          const res = await getNearbyBusinesses(
            pos.coords.latitude,
            pos.coords.longitude,
          );
          resolve(
            (res.nearbyDBBusinesses || []).map(
              (business): Business => ({
                ...business,
                location: business.location
                  ? {
                      latitude: business.location.coordinates[1],
                      longitude: business.location.coordinates[0],
                    }
                  : undefined,
              }),
            ),
          );
        } catch (err) {
          resolve([]);
        }
      });
    });
  };

  useEffect(() => {
    const fetchBusinesses = async () => {
      setIsLoading(true);
      try {
        let allBusinesses: Business[] = [];
        if (isAdmin) {
          const { businesses } = await fetchallBusinessApi(1, 1000);

          allBusinesses = businesses || [];
        } else if (searchTrigger && searchName.trim() !== "") {
          const keyword = searchName.trim();
          const nearbySearch = isNearbyKeyword(keyword);
          if (nearbySearch) {
            try {
              const { latitude, longitude } = await getUserLocation();
              const res = await getBusinessesByKeyword(
                keyword,
                1,
                10,
                searchCategory.trim(),
                latitude,
                longitude,
              );

              allBusinesses = res?.businesses || [];
            } catch (locationError) {
              console.error("Nearby search failed:", locationError);

              allBusinesses = [];
            }
          } else {
            const res = await getBusinessesByKeyword(
              keyword,
              1,
              10,
              searchCategory.trim(),
            );

            allBusinesses = res?.businesses || [];
          }
        } else {
          allBusinesses = await fetchNearbyBusinesses();
        }

        const allBusinessesWithPromoted = allBusinesses.map((biz) => ({
          ...biz,

          promoted: promotedBusinessIds.has(biz._id ?? ""),
        }));

        // Promoted first
        allBusinessesWithPromoted.sort(
          (a, b) => (b.promoted ? 1 : 0) - (a.promoted ? 1 : 0),
        );

        setBusinesses(allBusinessesWithPromoted);

        setSearchResults(allBusinessesWithPromoted);
      } catch (error) {
        console.error("Failed to fetch businesses:", error);

        setBusinesses([]);
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBusinesses();
  }, [isAdmin, searchTrigger, searchName, searchCategory, promotedBusinessIds]);
  useEffect(() => {
    if (isAdmin) return;
    setAdsLoading(true);

    (async () => {
      try {
        const { advertisements }: { advertisements: Ad[] } =
          await fetchAdvertisementsByStatus("accept", 1, 20);

        const now = new Date();
        const promoted = new Set(
          advertisements
            .filter(
              (ad) =>
                ad.type === "promoted" &&
                new Date(ad.startDate) <= now &&
                new Date(ad.endDate) >= now,
            )
            .map((ad) => ad.businessId),
        );

        setPromotedBusinessIds(promoted);

        const urls = advertisements
          .map((ad) =>
            Array.isArray(ad.images) && ad.images.length
              ? normaliseUrl(ad.images[0].url)
              : "",
          )
          .filter(Boolean);

        const left: string[][] = [[], []];
        const right: string[][] = [[], []];

        urls.forEach((url, i) => {
          if (i % 4 < 2) left[i % 2].push(url);
          else right[i % 2].push(url);
        });

        setAdsLeft(left);
        setAdsRight(right);
      } catch {
        setAdsLeft([
          ["/ads/placeholder-left.jpg"],
          ["/ads/placeholder-left.jpg"],
        ]);
        setAdsRight([
          ["/ads/placeholder-right.jpg"],
          ["/ads/placeholder-right.jpg"],
        ]);
      } finally {
        setAdsLoading(false);
      }
    })();
  }, [isAdmin]);

  useEffect(() => setCurrentPage(1), [searchTrigger]);
  useEffect(() => {
    setSelectedCategoryId(null);
  }, [searchTrigger]);

  const filtered = useMemo(() => {
    if (selectedCategoryId) {
      return businesses;
    }

    if (searchTrigger && searchName.trim() !== "") {
      return businesses;
    }

    const nameTerm = searchName.toLowerCase().trim();
    const catTerm = searchCategory.toLowerCase().trim();

    return businesses.filter((b) => {
      const nameMatch = (b.businessname?.toLowerCase() || "").includes(
        nameTerm,
      );

      let catName = "";
      const cat = b.categories?.[0];

      if (typeof cat === "string") {
        catName = cat.toLowerCase();
      } else if (typeof cat === "object" && cat?.en) {
        catName = cat.en.toLowerCase();
      }

      const catMatch = catName.includes(catTerm);
      return nameMatch && catMatch;
    });
  }, [
    businesses,
    searchTrigger,
    searchName,
    searchCategory,
    selectedCategoryId,
  ]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const slice = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleCategoryClick = async (categoryId: string) => {
    const isDeselecting = selectedCategoryId === categoryId;
    const newSelectedCategoryId = isDeselecting ? null : categoryId;

    setSelectedCategoryId(newSelectedCategoryId);
    setIsLoading(true);

    try {
      let fetchedBusinesses: Business[] = [];

      console.log(newSelectedCategoryId);

      if (newSelectedCategoryId) {
        const result = await fetchBizbyCategoryIdapi(
          newSelectedCategoryId,
          1,
          100,
        );
        fetchedBusinesses = result?.businesses || [];
      } else {
        fetchedBusinesses = isAdmin
          ? (await fetchallBusinessApi(1, 1000)).businesses
          : await fetchNearbyBusinesses();
      }

      const withPromoted = fetchedBusinesses.map((biz) => ({
        ...biz,
        promoted: promotedBusinessIds.has(biz._id ?? ""),
      }));

      withPromoted.sort((a, b) => (b.promoted ? 1 : 0) - (a.promoted ? 1 : 0));
      setBusinesses(withPromoted);
      // setMapBusinesses(withPromoted);
    } catch (err) {
      console.error("Error fetching businesses:", err);
      setBusinesses([]);
    } finally {
      setIsLoading(false);
      setCurrentPage(1);
    }
  };

  // Calculate rows
  const rowCount = Math.ceil(slice.length / 3);
  const visibleAdsLeft = adsLeft.slice(0, rowCount >= 3 ? 2 : 1);
  const visibleAdsRight = adsRight.slice(0, rowCount >= 3 ? 2 : 1);
  const scrollRef = useRef<HTMLDivElement>(null);

  const isMobile = useBreakpointValue({ base: true, md: false });
  return (
    <Box px={{ base: 4, md: 8 }} py={8} bg="blue.50" overflowX="hidden">
      {/* {isAdmin && (
        <Box ml={{ base: 0, md: 4, lg: 10 }} mb={2} px={{ base: 2, md: 0 }}>
          <Button colorScheme="blue" width={{ base: "100%", md: "auto" }}>
            {showCategories}
          </Button>
        </Box>
      )} */}

      {showCategories && (
        <Box
          ml={{ base: 0, md: 4 }}
          mr={{ base: 0, md: 4 }}
          mb={2}
          px={{ base: 2, md: 0, lg: 20 }}
          position="relative"
          width="100%"
        >
          <Flex align="center" direction="row" gap={2} flexWrap="nowrap">
            <Box
              ref={scrollRef}
              overflowX="auto"
              whiteSpace="nowrap"
              flex="1"
              sx={{
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              <Box display="inline-flex" gap="8px">
                <CategoryBar
                  category={category}
                  selectedCategoryId={selectedCategoryId}
                  handleCategoryClick={handleCategoryClick}
                  showCategories={showCategories}
                />
              </Box>
            </Box>
          </Flex>
        </Box>
      )}

      {isAdmin ? (
        <>
          <Grid
            templateColumns={{
              base: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            }}
            gap={4}
          >
            {isLoading ? (
              [...Array(3)].map((_, i) => (
                <BusinessCard
                  key={i}
                  id=""
                  isLoading
                  _id=""
                  businessname=""
                  businessdescription=""
                />
              ))
            ) : slice.length ? (
              slice.map((biz) => (
                <BusinessCard
                  key={biz._id}
                  id={biz._id}
                  slug={biz.slug}
                  name={biz.businessname}
                  categories={biz.categories?.slice(0, 3).map((c, i) => ({
                    _id: `${i}`,
                    categoryname:
                      typeof c === "string"
                        ? c
                        : typeof c === "object" && c?.en
                        ? c.en
                        : "",
                  }))}
                  website={biz.websitename}
                  imageUrl={biz.logo?.url || "/images/logo192.png"}
                  description={biz.businessdescription || "N/A"}
                  isLoading={false}
                  isAnyClicked={isAnyClicked}
                  setIsAnyClicked={setIsAnyClicked}
                  promotedBusinessIds={promotedBusinessIds.has(biz._id ?? "")}
                />
              ))
            ) : (
              <Text
                gridColumn="1 / -1"
                textAlign="center"
                color="gray.500"
                fontSize="lg"
              >
                No businesses found.
              </Text>
            )}
          </Grid>
          {totalPages > 1 && (
            <Flex justify="center" mt={8}>
              <Pagination
                currentPage={currentPage}
                totalPage={totalPages}
                onPageChange={setCurrentPage}
              />
            </Flex>
          )}
        </>
      ) : (
        <Flex direction={{ base: "column", lg: "row" }} gap={4}>
          {/* Left Ads */}
          <Box
            display={{ base: "none", lg: "flex" }}
            flexDir="column"
            gap={3}
            h={"380px"}
            w={{ lg: "200px" }}
            flexShrink={0}
            onClick={() => {
              sendGAEvent("event", "ad_click");
            }}
          >
            {!adsLoading &&
              visibleAdsLeft.map((imgs, idx) => (
                <AdSlot key={idx} images={imgs} />
              ))}
          </Box>

          {/* Main Business List */}
          <Box flex="1" minW={0}>
            <Box
              display={{ base: "block", lg: "none" }}
              mb={4}
              onClick={() => {
                sendGAEvent("event", "ad_click");
              }}
            >
              {!adsLoading && <AdSlot images={adsLeft[0]} />}
            </Box>

            <Grid
              templateColumns={{
                base: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              }}
              gap={4}
            >
              {isLoading ? (
                [...Array(3)].map((_, i) => (
                  <BusinessCard
                    key={i}
                    id=""
                    isLoading
                    _id=""
                    businessname=""
                    businessdescription=""
                  />
                ))
              ) : slice.length ? (
                slice.map((biz) => (
                  <BusinessCard
                    key={biz._id}
                    id={biz._id}
                    slug={biz.slug}
                    name={biz.businessname}
                    categories={biz.categories?.slice(0, 3).map((c, i) => ({
                      _id: `${i}`,
                      categoryname:
                        typeof c === "string"
                          ? c
                          : typeof c === "object" && c?.en
                          ? c.en
                          : "",
                    }))}
                    website={biz.websitename}
                    imageUrl={biz.logo?.url || "/images/logo.png"}
                    description={biz.businessdescription || "N/A"}
                    isLoading={false}
                    isAnyClicked={isAnyClicked}
                    setIsAnyClicked={setIsAnyClicked}
                    promotedBusinessIds={promotedBusinessIds.has(biz._id ?? "")}
                  />
                ))
              ) : (
                <Text
                  gridColumn="1 / -1"
                  textAlign="center"
                  color="gray.500"
                  fontSize="lg"
                >
                  No businesses found.
                </Text>
              )}
            </Grid>

            <Box
              display={{ base: "block", lg: "none" }}
              mt={6}
              onClick={() => {
                sendGAEvent("event", "ad_click");
              }}
            >
              {!adsLoading && <AdSlot images={adsRight[0]} />}
            </Box>

            {totalPages > 1 && (
              <Flex justify="center" mt={8}>
                <Pagination
                  currentPage={currentPage}
                  totalPage={totalPages}
                  onPageChange={setCurrentPage}
                />
              </Flex>
            )}
          </Box>

          {/* Right Ads */}
          <Box
            display={{ base: "none", lg: "flex" }}
            flexDir="column"
            gap={3}
            h={"380px"}
            w={{ lg: "200px" }}
            flexShrink={0}
            onClick={() => {
              sendGAEvent("event", "ad_click");
            }}
          >
            {!adsLoading &&
              visibleAdsRight.map((imgs, idx) => (
                <AdSlot key={idx} images={imgs} />
              ))}
          </Box>
        </Flex>
      )}

      <ChatBotWidget />
    </Box>
  );
};

export default BusinessList;
